import { create } from "zustand";
import { FIXTURE_PRO } from "../lib/fixtures";
import { IS_PREVIEW } from "../lib/pro-mode";
import type { ProRole } from "../lib/types";

/**
 * The pro's active viewing role (design §7.3). In production this derives from
 * the authenticated user's REAL role: the authed shell hydrates this store from
 * `profile.role` once it resolves (see `useRoleHydration`, SILL-346 item 4), and
 * the demo Agent/LO toggle is hidden — so the store settles to the real type and
 * stays there. The toggle survives only in preview/staging for demos.
 * Kept module-level so it persists across route navigation without prop-drilling.
 *
 * The role drives the LO-only underwriting gate on the detail view — but note
 * (design §4.8): the gate is ultimately SCOPE-driven, not role-driven. A
 * status_only payload simply won't carry the financial fields regardless of
 * role, so the component renders "Not shared at this tier". Role only decides
 * whether a CONNECTED full-financial client's full underwriting block is shown
 * vs. the agent's coarse credit band.
 */
interface RoleState {
  role: ProRole;
  setRole: (role: ProRole) => void;
}

export const useRoleStore = create<RoleState>((set) => ({
  // Prod: a neutral seed; `useRoleHydration` overwrites it with the agent's real
  // profile.role. Preview: seed the fixture role so the demo opens as that
  // persona and the preview-only toggle drives it from there.
  role: IS_PREVIEW ? FIXTURE_PRO.role : "agent",
  setRole: (role) => set({ role }),
}));
